import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

import { useStorefront } from "../../storefront/useStorefront";
import {
  STOREFRONT_CONFIGS,
  type StorefrontKey,
} from "../../storefront/storefront";
import {
  isSafeToCarryPath,
  buildStorefrontSwitchUrl,
} from "./storefrontSwitchHelpers";

type StorefrontOption = {
  key: StorefrontKey;
  shortName: string;
  badgeLabel: string;
  domain: string;
  description: string;
  requiresAgeGate?: boolean;
  logoPath: string;
};

export function StorefrontSwitcher() {
  const { config: currentStorefront } = useStorefront();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const options: StorefrontOption[] = useMemo(() => {
    return Object.values(STOREFRONT_CONFIGS).map((c) => ({
      key: c.key,
      shortName: c.shortName,
      badgeLabel: c.badgeLabel,
      domain: c.domain,
      description: c.description,
      requiresAgeGate: c.requiresAgeGate,
      logoPath: c.logoPath,
    }));
  }, []);

  const pathIsCarryable = useMemo(() => {
    // location.pathname is correct even if you’ll ultimately do a full-page nav
    return isSafeToCarryPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open]);

  function onSelect(targetKey: StorefrontKey) {
    if (targetKey === currentStorefront.key) return;

    // If the current route can’t be carried, we’ll fall back (your helper already does this).
    // But we can still gate “After Dark” explicitly.
    const targetConfig = STOREFRONT_CONFIGS[targetKey];
    const currentConfig = STOREFRONT_CONFIGS[currentStorefront.key];
    if (targetConfig?.requiresAgeGate && !currentConfig?.requiresAgeGate) {
      const storageKey = `bs_${targetKey}_confirmed`;
      const confirmed = window.localStorage.getItem(storageKey) === "true";
      if (!confirmed) {
        const ok = window.confirm(
          `${targetConfig.badgeLabel} is intended for adults (18+). Do you want to continue?`,
        );
        if (!ok) return;
        window.localStorage.setItem(storageKey, "true");
      }
    }

    const targetUrl = buildStorefrontSwitchUrl(targetKey);
    window.location.assign(targetUrl);
  }

  const currentLabel =
    currentStorefront.badgeLabel || currentStorefront.shortName;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className={[
          "inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold",
          "text-[color:var(--sf-text)] bg-[color:var(--sf-surface)]",
          "border border-[color:var(--sf-border)] rounded-[var(--sf-radius)]",
          "hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[color:var(--sf-focus-ring)]",
          "transition",
        ].join(" ")}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls="storefront-switcher-panel"
        aria-label="Switch storefront"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="inline-flex items-center gap-2">
          <img
            src={currentStorefront.logoPath}
            alt=""
            className="w-5 h-5 rounded"
            loading="lazy"
            decoding="async"
          />
          <span className="hidden sm:inline">{currentLabel}</span>
          <span className="sm:hidden">Storefront</span>
        </span>
        <ChevronDownIcon className="w-4 h-4 opacity-80" />
      </button>

      {open && (
        <div
          id="storefront-switcher-panel"
          ref={panelRef}
          role="region"
          aria-label="Storefront options"
          className={[
            "absolute right-0 mt-2 z-50 w-[min(24rem,calc(100vw-1.5rem))]",
            "max-h-[min(70vh,36rem)] overflow-y-auto",
            "bg-[color:var(--sf-surface)] border border-[color:var(--sf-border)]",
            "rounded-[var(--sf-radius)] shadow-[var(--sf-shadow)]",
            "p-2",
          ].join(" ")}
        >
          <div className="px-2 pb-2">
            <div className="text-xs text-[color:var(--sf-mutedText)]">
              Switch storefront
            </div>
            {!pathIsCarryable && (
              <div className="mt-1 text-xs text-[color:var(--sf-mutedText)]">
                You’ll be sent to{" "}
                <span className="font-semibold">Auctions</span> on the other
                storefront.
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1">
            {options.map((opt) => {
              const isCurrent = opt.key === currentStorefront.key;

              return (
                <button
                  key={opt.key}
                  type="button"
                  disabled={isCurrent}
                  onClick={() => onSelect(opt.key)}
                  className={[
                    "w-full text-left px-2 py-2 rounded-[calc(var(--sf-radius)-6px)]",
                    "flex items-start gap-3",
                    isCurrent
                      ? "opacity-70 cursor-default"
                      : "hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-[color:var(--sf-focus-ring)]",
                  ].join(" ")}
                >
                  <img
                    src={opt.logoPath}
                    alt=""
                    className="w-9 h-9 rounded"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-sm font-semibold text-[color:var(--sf-text)] leading-tight break-words">
                        {opt.shortName}
                      </span>
                      <span
                        className={[
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs border",
                          opt.requiresAgeGate
                            ? "border-purple-300/60 bg-purple-500/15 text-[color:var(--sf-text)]"
                            : "border-[color:var(--sf-border)] bg-black/5 text-[color:var(--sf-mutedText)]",
                        ].join(" ")}
                      >
                        {opt.requiresAgeGate ? "18+" : opt.badgeLabel}
                      </span>
                      {isCurrent && (
                        <span className="text-xs text-[color:var(--sf-mutedText)]">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs leading-snug text-[color:var(--sf-mutedText)] break-words">
                      {opt.description} · {opt.domain}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="pt-2 px-2">
            <button
              type="button"
              className="text-xs text-[color:var(--sf-mutedText)] hover:underline"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
